<script lang="ts">
    import cn from 'classnames';
    import { IconName, SvgIcon, SvgIconTypes } from '@uikit/svg-icon';

    import { type Action, setActionFunc } from '@uikit/actions';

    import s from './input.module.css';
    import { type Attrs, type IconData, Size, State, Type } from './types';

    export let name: NullableString = undefined;
    export let value: NullableString = undefined;
    export let type: NullableType<Type> = undefined;
    export let size: NullableType<Size> = undefined;
    export let label: NullableString = undefined;
    export let placeholder: NullableString = undefined;
    export let maxlength: NullableNumber = undefined;
    export let counter: boolean = Boolean(maxlength);
    export let hint: NullableString = undefined;
    export let caption: NullableString = undefined;
    export let description: NullableString = undefined;
    export let descriptionLight: boolean = false;
    export let state: NullableType<State> = undefined;
    export let message: NullableString = undefined;
    export let required: boolean = false;
    export let disabled: boolean = false;
    export let readonly: boolean = false;
    export let dropdown: boolean = false;
    export let isDropdownOpened: boolean = false;
    export let inline: boolean = false;
    export let iconLeft: NullableType<IconData> = undefined;
    export let iconRight: NullableType<IconData> = undefined;
    export let classes: NullableString = '';
    export let svgLeftClasses: NullableString = '';
    export let svgRightClasses: NullableString = '';
    export let labelClasses: NullableString = undefined;
    export let inputClasses: NullableString = undefined;
    export let attrs: NullableType<Attrs> = {};
    export let input: NullableType<HTMLInputElement> = undefined;
    export let fullWidth: boolean = false;
    export let hideEye: boolean = false;
    export let captionOnNextLine: boolean = false;
    export let action: Action | undefined = undefined;

    $: ({ actionFunc, actionParams } = setActionFunc(action));
    const initialType = type;

    $: if (maxlength) {
        value = (value || '').slice(0, maxlength);
    }
    $: captionOnNextLine = inline ? true : captionOnNextLine;

    function typeAction(node: HTMLInputElement) {
        if (type) {
            node.type = type;
        }
    }

    function onClickEye() {
        type = type === Type.password ? Type.text : Type.password;

        if (input) {
            input.type = type;
        }
    }
</script>

<div
    use:actionFunc={actionParams}
    class={cn(
        s.main,
        fullWidth && s.fullWidth,
        inline && s.inline,
        size && s[`size-${size}`],
        state && s[`state-${state}`],
        disabled && s.disabled,
        (iconLeft || iconRight) && s.hasIcon,
        classes,
    )}
>
    {#if label}
        <div class={cn(s.header, { [s.captionOnNextLine]: captionOnNextLine }, labelClasses)}>
            <div class={s.headerLeft}>
                <label for={name} class={s.label}>
                    <slot name="label">
                        {label}
                    </slot>
                </label>
                {#if required}
                    <span class={s.required}>*</span>
                {/if}
                {#if hint}
                    <div class={cn(s.icon, s.hint)} data-tooltip={hint}>
                        <SvgIcon classes={s.icon} icon={IconName.HintFilled} />
                    </div>
                {/if}
            </div>
            {#if caption}
                <div class={s.caption}>
                    <slot name="caption">
                        {@html caption}
                    </slot>
                </div>
            {/if}
        </div>
    {/if}
    <div class={cn(s.data, fullWidth && s.fullWidth)}>
        <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
        <div class={cn(s.input, inputClasses)} on:click>
            {#if iconLeft}
                <div class={cn(s.icon, s.left)}>
                    <SvgIcon icon={iconLeft} classes={svgLeftClasses} />
                </div>
            {/if}
            <slot>
                <slot name="before-input" />
                <div class={s.inputWrapper}>
                    <input
                        id={name}
                        {name}
                        class={s.text}
                        {placeholder}
                        {readonly}
                        {disabled}
                        {maxlength}
                        {...attrs}
                        on:focus
                        on:blur
                        on:input
                        bind:this={input}
                        bind:value
                        use:typeAction
                    />
                    <slot name="after-input" />
                </div>
            </slot>
            <slot name="hidden-input" />
            {#if counter && maxlength}
                <div class={s.counter}>
                    {maxlength - (value || '').length}
                </div>
            {/if}
            {#if state === State.success}
                <div class={cn(s.icon, s.success)}>
                    <SvgIcon icon={IconName.Check} />
                </div>
            {:else if state === State.error}
                <div class={cn(s.icon, s.error)}>
                    <SvgIcon icon={IconName.Alert} />
                </div>
            {/if}
            {#if initialType === Type.password && !hideEye}
                <div
                    class={cn(s.icon, s.eye)}
                    on:click={onClickEye}
                    on:keydown={onClickEye}
                    on:keyup={onClickEye}
                    on:keypress={onClickEye}
                >
                    <SvgIcon icon={type === Type.password ? IconName.Eye : IconName.EyeOff} />
                </div>
            {/if}
            {#if iconRight}
                <div class={cn(s.icon, s.right)}>
                    <SvgIcon icon={iconRight} classes={svgRightClasses} />
                </div>
            {/if}
            {#if dropdown}
                <div class={cn(s.icon, s.arrowDown)}>
                    <SvgIcon
                        rotationAngle={isDropdownOpened
                            ? SvgIconTypes.RotationAngle.OneEighty
                            : SvgIconTypes.RotationAngle.Zero}
                        icon={IconName.ArrowDownShort}
                    />
                </div>
            {/if}
        </div>
        {#if message}
            <div class={cn(s.message, state && s[`state-${state}`])}>
                <slot name="message">
                    {message}
                </slot>
            </div>
        {/if}
        {#if description}
            <div class={cn(s.description, descriptionLight && s.descriptionLight)}>
                <slot name="description">
                    {description}
                </slot>
            </div>
        {/if}
    </div>
</div>
